import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import '../../styles/css/summary_modal.css';
import { SummaryBody } from './SummaryBody';
import { SummaryToolbar } from './SummaryToolbar';
import { SummaryButtonGroup } from './SummaryButtonGroup';
import { useAppSelector } from '../../redux/store';
import { useSummary, SummarizePayload } from '../../hooks/useSummary';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { EditorState } from 'draft-js';

type Props = {
  onClose: () => void;
  data?: string;
  editorState?: EditorState;
};

export interface SummaryModalRef {
  handleOnClose: () => void;
}

// Export function for downloading summary
const exportSummary = (summary: string, sessionId: string) => {
  const blob = new Blob([summary], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `summary_${sessionId}_${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const SummaryModal = forwardRef<SummaryModalRef, Props>(({ onClose, data, editorState }, ref) => {
  const [summarizePayload, setSummarizePayload] = useState<SummarizePayload>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { sessionId, sessionLockKey } = useAppSelector((state) => state.app.editorLock);

  const {
    isLoadingPrompts,
    isLoadingSummary,
    isPending,
    summaryInProgress,
    availablePrompts,
    summary,
    summaryExists,
    availableModels,
    startSummarize,
    initSummarize,
  } = useSummary({
    sessionId: sessionId?.toString() || null,
    textContent: data,
    editorState: editorState,
    sessionLockKey: sessionLockKey || null,
  });

  useEffect(() => {
    initSummarize();
  }, [initSummarize]);

  const summarizeEnabled = !summaryInProgress && !!summarizePayload;

  const handleOnSummarize = useCallback(async () => {
    if (!summarizePayload) return;
    await startSummarize(summarizePayload);
  }, [summarizePayload, startSummarize]);

  const handleOnExport = useCallback(async () => {
    if (!summary) return;
    exportSummary(summary, sessionId!.toString());
  }, [summary, sessionId]);

  const handleOnClose = useCallback(() => {
    if (summaryInProgress) {
      setShowConfirmDialog(true);
    } else {
      onClose();
      setSummarizePayload(undefined);
    }
  }, [onClose, summaryInProgress]);

  useImperativeHandle(
    ref,
    () => ({
      handleOnClose,
    }),
    [handleOnClose]
  );

  const handleConfirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    onClose();
    setSummarizePayload(undefined);
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  return (
    <>
      <div className="summary_modal_content_wrapper">
        <div className="summary_modal_header">
          <div className="summary_modal_header_title">Povzetek</div>
        </div>
        <SummaryToolbar
          isLoading={isLoadingPrompts}
          summarizePayload={summarizePayload}
          setSummarizePayload={setSummarizePayload}
          availableStages={availablePrompts.map((prompt) => ({
            id: prompt.id,
            name: prompt.namen,
            isDefault: prompt.is_default,
          }))}
          availableModels={availableModels}
        />
        <SummaryButtonGroup
          summaryExists={summaryExists}
          isEnabled={summarizeEnabled}
          isPending={isPending}
          summaryInProgress={summaryInProgress}
          onSummarize={handleOnSummarize}
          onExport={handleOnExport}
        />
        <SummaryBody summary={summary} isLoading={isLoadingSummary} />
        <div className="summary_modal_header_close">
          <button onClick={handleOnClose} className="modal_button_disaggre">
            Zapri
          </button>
        </div>
      </div>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Potrditev zapiranja</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Ali ste prepričani, da želite zapreti? Povzetek se še vedno ustvarja.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button onClick={handleCancelClose} className="button_secondary">
            Nazaj
          </button>
          <button onClick={handleConfirmClose} className="button_primary">
            Zapri
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
});
